import React, { useRef, useState } from "react";
import { DownloadOutlined, UploadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import Button from "src/Components/Button";
import Modal from "src/Components/Modal/Modal";
import { exportTaskEditExcel, importTaskEditExcel, previewTaskEditExcel } from "src/Requests/automaticMarketMaking";
import { IMarketmakingTaskSearchParam, ITaskExcelResult, ITaskExcelPreview } from "src/Interfaces/automaticMarketMaking";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface Props {
  filters: IMarketmakingTaskSearchParam;
  onUpdated: () => void;
}

export default function TaskExcelActions({ filters, onUpdated }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState<"download" | "upload" | "confirm" | null>(null);
  const [result, setResult] = useState<ITaskExcelResult | null>(null);
  const [preview, setPreview] = useState<{ file: File; data: ITaskExcelPreview } | null>(null);
  const changedRows = preview?.data.rows.filter(row => row.willChange && !row.error) ?? [];
  const errorCount = preview?.data.rows.filter(row => row.error).length ?? 0;

  const download = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy("download");
    try {
      const file = await exportTaskEditExcel(filters);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      // Download requests receive error bodies as blobs too.
      if (error?.response?.data instanceof Blob) {
        try { error.response.data = JSON.parse(await error.response.data.text()); } catch { /* standard error handler */ }
      }
      packetErrorHandler(error);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busyRef.current) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("فایل اکسل ویرایش با پسوند xlsx را انتخاب کنید.");
      return;
    }
    busyRef.current = true;
    setBusy("upload");
    setResult(null);
    try {
      const data = await previewTaskEditExcel(file);
      setPreview({ file, data });
    } catch (error) {
      packetErrorHandler(error);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const confirm = async () => {
    if (!preview || busyRef.current || changedRows.length === 0) return;
    busyRef.current = true;
    setBusy("confirm");
    try {
      const response = await importTaskEditExcel(preview.file, changedRows.map(row => row.row));
      setPreview(null);
      setResult(response);
      if (response.updatedCount > 0) onUpdated();
    } catch (error) {
      packetErrorHandler(error);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" aria-busy={busy !== null}>
        <div>
          <Button variant="outlined" disabled={busy !== null || preview !== null} loading={busy === "download"} onClick={download}
            className="!text-xs !py-2"
            text={<span className="flex items-center gap-2"><DownloadOutlined />دانلود اکسل ویرایش</span>} />
        </div>
        <div>
          <Button variant="contained" disabled={busy !== null || preview !== null} loading={busy === "upload"} onClick={() => inputRef.current?.click()}
            className="!text-xs !py-2"
            text={<span className="flex items-center gap-2"><UploadOutlined />آپلود اکسل ویرایش</span>} />
        </div>
        <input ref={inputRef} type="file" accept=".xlsx" className="hidden" aria-label="فایل اکسل ویرایش تسک" onChange={upload} disabled={busy !== null || preview !== null} />
      </div>
      {preview && (
        <Modal title="پیش‌نمایش ویرایش با اکسل" onClose={() => { if (!busyRef.current) setPreview(null); }} modalBoxClassName="w-[1200px] !min-w-0 max-w-[95vw]" UI={
          <div className="flex flex-col gap-4 w-full pt-2" dir="rtl" aria-busy={busy === "confirm"}>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-emerald-700">{changedRows.length} ردیف دارای تغییر</span>
              <span className="text-gray-600">{preview.data.rows.length - changedRows.length - errorCount} ردیف بدون تغییر</span>
              <span className="text-amber-700">{errorCount} ردیف دارای خطا</span>
            </div>
            <div className="max-h-[55vh] overflow-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-20 bg-gray-100 text-gray-600">
                  <tr>
                    <th className="p-3 sticky right-0 z-30 bg-gray-100">وضعیت ویرایش</th>
                    <th className="p-3">ردیف اکسل</th>
                    {preview.data.headers.map(header => <th key={header} className="p-3">{header}</th>)}
                    <th className="p-3">علت خطا</th>
                  </tr>
                </thead>
                <tbody>{preview.data.rows.map(row => (
                  <tr key={row.row} className="border-t border-gray-200">
                    <td className="p-3 sticky right-0 z-10 bg-white">
                      <span className={`inline-block rounded px-2 py-1 text-xs ${row.error ? "bg-amber-50 text-amber-800" : row.willChange ? "bg-emerald-50 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                        {row.error ? "خطادار؛ اعمال نمی‌شود" : row.willChange ? "تغییر می‌کند" : "بدون تغییر"}
                      </span>
                    </td>
                    <td className="p-3 tabular-nums">{row.row}</td>
                    {row.values.map((value, index) => {
                      const field = preview.data.fields?.[index];
                      const changed = row.willChange && !row.error && field && row.changedFields?.includes(field);
                      return <td key={field ?? index} className={`p-3 tabular-nums ${changed ? "!bg-[#f0f9ff] !text-[#0c4a6e] !font-bold" : ""}`}>{value || "—"}</td>;
                    })}
                    <td className="p-3 !whitespace-normal !text-right min-w-[240px] leading-7">{row.error || "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-3 justify-end">
              <div><Button variant="outlined" text="انصراف" disabled={busy !== null} onClick={() => setPreview(null)} /></div>
              <div><Button variant="contained" text={`تأیید و اعمال ${changedRows.length} تغییر`} disabled={busy !== null || changedRows.length === 0} loading={busy === "confirm"} onClick={confirm} /></div>
            </div>
          </div>
        } />
      )}
      {result && (
        <Modal title="نتیجه ویرایش با اکسل" onClose={() => setResult(null)} modalBoxClassName="w-[680px] !min-w-0 max-w-[95vw]" UI={
          <div className="flex flex-col gap-5 w-full pt-2" dir="rtl">
            <div>
              <h3 className="font-bold text-base text-gray-800">نتیجهٔ ویرایش با اکسل</h3>
              <p className="text-xs text-gray-500 mt-2">ردیف‌های دارای خطا تغییری نکرده‌اند.</p>
            </div>
            {result.updatedCount === 0 && result.unchangedCount > 0 && result.errors.length === 0 && (
              <p className="text-sm text-gray-600">اطلاعات فایل با مقادیر فعلی یکسان است؛ نیازی به ویرایش نبود.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 flex items-center gap-3">
                <CheckCircleOutlined className="text-xl" /><span><strong>{result.updatedCount}</strong> تسک ویرایش شد</span>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700 flex items-center gap-3">
                <span><strong>{result.unchangedCount ?? "—"}</strong> تسک بدون تغییر</span>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 flex items-center gap-3">
                <ExclamationCircleOutlined className="text-xl" /><span><strong>{result.errors.length}</strong> ردیف دارای خطا</span>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-[360px] overflow-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm text-right">
                  <thead className="sticky top-0 z-10 bg-gray-100 text-gray-600">
                    <tr><th className="p-3 whitespace-nowrap">ردیف اکسل</th><th className="p-3 whitespace-nowrap">شناسه تسک</th><th className="p-3">علت خطا</th></tr>
                  </thead>
                  <tbody>{result.errors.map(error => (
                    <tr key={error.row} className="border-t border-gray-100">
                      <td className="p-3 tabular-nums">{error.row}</td><td className="p-3 tabular-nums">{error.taskId ?? "—"}</td><td className="p-3 leading-7 !whitespace-normal break-words !text-right">{error.message}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <div className="self-end w-24"><Button variant="outlined" text="بستن" onClick={() => setResult(null)} /></div>
          </div>
        } />
      )}
    </>
  );
}
